import React, { useState } from 'react';
import { Paper, Typography } from '@mui/material';
import RuleForm from './RuleForm';
import { RuleValidationResult, TargetRule } from '../types';
import { isMoreSpecific } from '../services/validationRuleService';
import TargetRuleLayout from '../TargetRuleLayout';
import { PositionType } from 'domain/shared/employee';
import type { Technology } from 'domain/shared/career';

interface TargetRuleBuilderProps {
  rules: TargetRule[];
  onAddRule: (rule: Omit<TargetRule, 'id'>) => void;
  onDeleteRule: (id: string) => void;
  onEditRule: (id: string, updatedRule: Omit<TargetRule, 'id'>) => void;
  remainingCount: number;
  validateRule: (rule: Omit<TargetRule, 'id'>, editingId?: string) => RuleValidationResult;
  positions: PositionType[];
  technologies: Technology[];
}

const TargetRuleBuilder: React.FC<TargetRuleBuilderProps> = ({
  rules,
  onAddRule,
  onDeleteRule,
  onEditRule,
  remainingCount,
  validateRule,
  positions,
  technologies,
}) => {
  const [newRule, setNewRule] = useState<Omit<TargetRule, 'id'>>({ capacity: 0 });

  const validation = validateRule(newRule);

  const hasAvailableCapacityInParents = rules.some(rule => {
    const nestedRules = rules.filter(r => r.id !== rule.id && isMoreSpecific(r, rule));
    const nestedCount = nestedRules.reduce((sum, r) => sum + r.capacity, 0);
    return nestedCount < rule.capacity;
  });

  const isFormDisabled = remainingCount <= 0 && !hasAvailableCapacityInParents;

  const handleRuleChange = (field: string, value: any) => {
    if (field) {
      setNewRule(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleAddRule = () => {
    if (validation.valid) {
      onAddRule(newRule);
    }
  };

  return (
    <>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h4" mb={3} color="primary">
          Create qualification structure rule
        </Typography>
        <RuleForm
          rule={newRule}
          validation={validation}
          onChange={handleRuleChange}
          onSubmit={handleAddRule}
          disabled={isFormDisabled}
          positions={positions}
          technologies={technologies}
          submitLabel="Add"
        />
      </Paper>

      <TargetRuleLayout
        rules={rules}
        validateRule={validateRule}
        positions={positions}
        technologies={technologies}
        onDeleteRule={onDeleteRule}
        onEditRule={onEditRule}
      />
    </>
  );
};

export default TargetRuleBuilder;
